import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isHasAccess } from 'features/validateAccess';
import { AccountStatus, accountsService } from 'entities/account';
import { withSuspense } from 'shared/lib/react';
import { EmptyGroup } from 'shared/ui/EmptyGroup';
import { Loader } from 'shared/ui/Loader';
import { RcPagination } from 'shared/ui/RcPagination';

import { DEFAULT_SORT_ORDER, PAGE_SIZE } from '../../AccountList.const';
import {
  getLocalEditAccountPermission,
  getLocalReadProjectPermission,
  GLOBAL_EDIT_ACCOUNT_PERMISSION,
  GLOBAL_PROJECT_READ_PERMISSION,
} from '../../AccountList.permissions';
import { AccountItem } from '../AccountItem';

import styles from './AccountList.module.scss';

const AccountList = ({
  page,
  setPage,
  status,
}: {
  page: number;
  setPage: (page: number) => void;
  status: AccountStatus;
}): React.ReactElement => {
  const { t } = useTranslation('Account');

  const {
    data: { content: accountList, ...accountListAux },
  } = useSuspenseQuery(
    accountsService.queryOptions({
      page: page - 1,
      size: PAGE_SIZE,
      filter: status,
      sort: DEFAULT_SORT_ORDER,
    }),
  );

  const canEditAllAccounts = isHasAccess(GLOBAL_EDIT_ACCOUNT_PERMISSION);
  const canSeeAllProjects = isHasAccess(GLOBAL_PROJECT_READ_PERMISSION);

  return (
    <section className={styles.accounts}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.namePlaceholder}>&nbsp;</div>
          <div className={styles.status}>{t('AccountList.status')}</div>
          <div className={styles.projectsCount}>{t('AccountList.projects')}</div>
          <div className={styles.buttonPlacehoder}>&nbsp;</div>
        </div>
      </header>
      <ul className={styles.list} data-testid="data-list">
        {accountList.length > 0 ? (
          accountList.map((account) => {
            const canEditAccount = canEditAllAccounts || isHasAccess(getLocalEditAccountPermission(account.id));
            const canSeeProjects = canSeeAllProjects || isHasAccess(getLocalReadProjectPermission(account.id));

            return (
              <AccountItem
                key={account.id}
                accountId={account.id}
                disableLink={!canSeeProjects}
                isActive={account.active}
                name={account.name}
                logoUrl={account.logoUrl}
                projectsCount={account.projectsCount}
                hideEditButton={!canEditAccount}
                archived={!!account.archivedAt}
              />
            );
          })
        ) : (
          <EmptyGroup />
        )}
      </ul>
      <div className={styles.pagination}>
        <RcPagination
          pageSize={PAGE_SIZE}
          setCurrentPage={(pageNum) => {
            setPage(pageNum);

            setTimeout(() => {
              document.querySelector('main')?.scrollTo({ top: 0, behavior: 'smooth' });
            }, 0);
          }}
          dataSize={accountListAux?.totalElements ?? 0}
          current={page}
        />
      </div>
    </section>
  );
};

export default withSuspense(AccountList, { fallback: <Loader /> });
